import './new-form-select-field.scss';
import './new-left-navigation.scss';

import { Controller, useFormContext } from 'react-hook-form';
import { FormHelperText, MenuItem, SxProps, Theme } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useEffect, useMemo } from 'react';

import { CircularLoader } from './circular-loader';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';

export type Value = string | number | null;
export interface ListOption<D, V extends Value = string> {
  value: V;
  label: string;
  data: D;
  disabled?: boolean;
}

export type SetStateType<T> = boolean | ((value: T) => void);
interface NewFormSelectFieldProps<D = unknown, V extends Value = string> {
  setIsPartnered?: SetStateType<boolean>;
  label?: string | null;
  className?: string;
  placeholder?: string;
  required?: boolean;
  value?: string | null;
  name: string;
  containerWidth?: number;
  options?: D[];
  onChange?: (event: SelectChangeEvent<NonNullable<V>>) => void;
  mapToOption?: (
    item: D
  ) => Omit<ListOption<D, V | null>, 'data'> &
    Partial<Pick<ListOption<D, V | null>, 'data'>>;
  isLoading?: boolean;
  onChangeOption?: (item: ListOption<D, V>) => void;
  hideLabel?: boolean;
  debug?: boolean;
  disabled?: boolean;
  tooltip?: string;
  hideBorders?: boolean;
  sx?: SxProps<Theme> | undefined;
  dataTestId?: string;
}

export const NewFromSelectField = <D, V extends Value = string>({
  name,
  label = name,
  required,
  containerWidth = 6,
  options,
  mapToOption,
  isLoading,
  placeholder,
  onChangeOption,
  hideLabel = false,
  value: valueProp,
  tooltip,
  sx,
  debug,
  hideBorders = false,
  dataTestId,
  ...props
}: NewFormSelectFieldProps<D, V>) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const listOptions = useMemo((): ListOption<D, V>[] => {
    if (isLoading) {
      return [];
    }

    if (!options?.length) {
      return [];
    }

    if (mapToOption) {
      return options.map((item) => {
        const option = mapToOption(item);
        option.data = item;
        return option as ListOption<D, V>;
      });
    }

    return options as ListOption<D, V>[];
  }, [isLoading, mapToOption, options]);

  const error = errors[name];

  const currentValue = valueProp ?? watch(name);

  const selectedListOption = listOptions.find(
    (option) => option.value === currentValue || option.label === currentValue
  );

  useEffect(() => {
    if (!selectedListOption) {
      return;
    }
    if (selectedListOption.value !== currentValue) {
      setValue(name, selectedListOption.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [setValue, name, selectedListOption, currentValue]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, ...otherField } }) => {
        return (
          <FormFieldContainer md={containerWidth} sx={sx}>
            {hideLabel === false && (
              <NewConvergeInputLabel
                label={label}
                required={required}
                className={errors[name] ? 'error-message' : ''}
                tooltip={tooltip}
              />
            )}
            <Select
              fullWidth
              disabled={isLoading}
              size="small"
              displayEmpty
              placeholder={placeholder}
              className={`new-select-field ${hideBorders ? 'no-borders' : ''}`}
              sx={
                hideBorders
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }
                  : undefined
              }
              onChange={(event) => {
                onChange(event);
                if (onChangeOption) {
                  const foundOption = listOptions.find(
                    (listOption) => listOption.value === event.target.value
                  );
                  if (!foundOption) {
                    throw new Error(
                      `No option found for "${event.target.value}"`
                    );
                  }
                  onChangeOption(foundOption);
                }
              }}
              {...otherField}
              {...props}
              value={selectedListOption?.value ?? ''}
              error={!!error}
              inputProps={{
                ...(dataTestId && { 'data-testid': dataTestId }),
              }}
            >
              <MenuItem disabled value="">
                <em className="placeholder">{placeholder}</em>
              </MenuItem>
              {isLoading ? (
                <div style={{ margin: 10 }}>
                  <CircularLoader className="circular-loader-style" />
                </div>
              ) : (
                listOptions.map((item, index) => {
                  return (
                    <MenuItem
                      className="dropdown-item"
                      key={`${item.value ?? index}`}
                      value={item.value as string}
                      disabled={item.disabled}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })
              )}
            </Select>
            {error && (
              <FormHelperText
                error
                className="form-select-form-helper-text-error"
              >
                {(error.message as string) || 'This field is required'}
              </FormHelperText>
            )}
          </FormFieldContainer>
        );
      }}
    />
  );
};
